import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Package, Loader2, AlertCircle } from 'lucide-react';
import { usePartnerOrders, PartnerOrder } from '@/hooks/usePartnerOrders';
import { OrderCard } from '@/components/partner/orders/OrderCard';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { toast } from 'sonner';
import { isToday, startOfWeek, endOfWeek, addWeeks, addHours, isWithinInterval, parseISO, isBefore, startOfDay } from 'date-fns';

type DeliveryFilter = 'all' | 'within-24hrs' | 'today' | 'this-week' | 'next-week' | 'overdue';

const PartnerOrders = () => {
  const { orders, isLoading, markDelivered, scheduleDelivery } = usePartnerOrders();
  const { isReadOnly } = useReadOnly();
  const [activeTab, setActiveTab] = useState('active');
  const [deliveringOrderId, setDeliveringOrderId] = useState<string | null>(null);
  const [deliveryFilter, setDeliveryFilter] = useState<DeliveryFilter>('all');

  const activeOrders = orders.filter(o => ['assigned', 'accepted', 'in_progress'].includes(o.status));
  const completedOrders = orders.filter(o => o.status === 'delivered');
  const cancelledOrders = orders.filter(o => o.status === 'cancelled');

  // Filter active orders by delivery date
  const filterOrders = (ordersToFilter: PartnerOrder[], filter: DeliveryFilter): PartnerOrder[] => {
    const today = startOfDay(new Date());
    const thisWeekStart = startOfWeek(today, { weekStartsOn: 0 });
    const thisWeekEnd = endOfWeek(today, { weekStartsOn: 0 });
    const nextWeekStart = addWeeks(thisWeekStart, 1);
    const nextWeekEnd = addWeeks(thisWeekEnd, 1);

    switch (filter) {
      case 'within-24hrs': {
        const next24Hours = addHours(new Date(), 24);
        return ordersToFilter.filter(o => {
          const deliveryDate = parseISO(o.delivery_date);
          return isWithinInterval(deliveryDate, { start: new Date(), end: next24Hours });
        });
      }
      case 'today':
        return ordersToFilter.filter(o => isToday(parseISO(o.delivery_date)));
      case 'this-week':
        return ordersToFilter.filter(o =>
          isWithinInterval(parseISO(o.delivery_date), { start: thisWeekStart, end: thisWeekEnd })
        );
      case 'next-week':
        return ordersToFilter.filter(o =>
          isWithinInterval(parseISO(o.delivery_date), { start: nextWeekStart, end: nextWeekEnd })
        );
      case 'overdue':
        return ordersToFilter.filter(o => {
          // For windowed orders (cleaning), overdue = window end has passed
          const overdueDate = o.delivery_window_end || o.delivery_date;
          return isBefore(parseISO(overdueDate), today);
        });
      default:
        return ordersToFilter;
    }
  };

  // Calculate filter counts
  const filterCounts = useMemo(() => {
    const today = startOfDay(new Date());
    const thisWeekStart = startOfWeek(today, { weekStartsOn: 0 });
    const thisWeekEnd = endOfWeek(today, { weekStartsOn: 0 });
    const nextWeekStart = addWeeks(thisWeekStart, 1);
    const nextWeekEnd = addWeeks(thisWeekEnd, 1);

    const next24Hours = addHours(new Date(), 24);

    return {
      all: activeOrders.length,
      'within-24hrs': activeOrders.filter(o => {
        const deliveryDate = parseISO(o.delivery_date);
        return isWithinInterval(deliveryDate, { start: new Date(), end: next24Hours });
      }).length,
      today: activeOrders.filter(o => isToday(parseISO(o.delivery_date))).length,
      'this-week': activeOrders.filter(o =>
        isWithinInterval(parseISO(o.delivery_date), { start: thisWeekStart, end: thisWeekEnd })
      ).length,
      'next-week': activeOrders.filter(o =>
        isWithinInterval(parseISO(o.delivery_date), { start: nextWeekStart, end: nextWeekEnd })
      ).length,
      overdue: activeOrders.filter(o => {
        const overdueDate = o.delivery_window_end || o.delivery_date;
        return isBefore(parseISO(overdueDate), today);
      }).length,
    };
  }, [activeOrders]);

  const filteredActiveOrders = useMemo(() =>
    filterOrders(activeOrders, deliveryFilter),
    [activeOrders, deliveryFilter]
  );

  const handleMarkDelivered = async (orderId: string, file: File) => {
    setDeliveringOrderId(orderId);
    try {
      await markDelivered.mutateAsync({ orderId, file });
      toast.success('Order marked as delivered. Customer has been notified.');
    } catch (error) {
      console.error('Failed to mark delivered:', error);
      const message = error instanceof Error ? error.message : 'Failed to update order';
      toast.error(message);
    } finally {
      setDeliveringOrderId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const filterButtons: { key: DeliveryFilter; label: string; urgent?: boolean }[] = [
    { key: 'all', label: 'All' },
    { key: 'overdue', label: 'Overdue', urgent: true },
    { key: 'within-24hrs', label: 'Within 24hrs', urgent: true },
    { key: 'today', label: 'Due Today' },
    { key: 'this-week', label: 'This Week' },
    { key: 'next-week', label: 'Next Week' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Orders</h1>
        <p className="text-muted-foreground">
          Manage your delivery orders
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => {
        setActiveTab(value);
        setDeliveryFilter('all'); // Reset filter when switching tabs
      }}>
        <TabsList>
          <TabsTrigger value="active">
            Active ({activeOrders.length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({completedOrders.length})
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            Cancelled ({cancelledOrders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4 space-y-4">
          {/* Filter buttons */}
          <div className="flex flex-wrap gap-2">
            {filterButtons.map(({ key, label, urgent }) => {
              const count = filterCounts[key];
              const isActive = deliveryFilter === key;
              const showUrgent = urgent && count > 0;

              return (
                <Button
                  key={key}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDeliveryFilter(key)}
                  className={showUrgent && !isActive ? "border-destructive text-destructive hover:bg-destructive/10" : ""}
                >
                  {showUrgent && <AlertCircle className="h-3 w-3 mr-1" />}
                  {label} ({count})
                </Button>
              );
            })}
          </div>

          {filteredActiveOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">
                    {deliveryFilter === 'all' ? 'No active orders' : `No orders ${deliveryFilter === 'overdue' ? 'overdue' : `due ${deliveryFilter.replace('-', ' ')}`}`}
                  </h3>
                  <p className="text-sm">
                    {deliveryFilter === 'all'
                      ? 'New orders will appear here when customers place requests.'
                      : 'Try selecting a different filter to see more orders.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {filteredActiveOrders.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onMarkDelivered={(file) => handleMarkDelivered(order.id, file)}
                  onScheduleDelivery={async (date) => {
                    await scheduleDelivery.mutateAsync({ orderId: order.id, date });
                    toast.success('Delivery date scheduled');
                  }}
                  isDelivering={deliveringOrderId === order.id}
                  readOnly={isReadOnly}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-4">
          {completedOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No completed orders yet</h3>
                  <p className="text-sm">Delivered orders will appear here.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {completedOrders.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onMarkDelivered={async () => {}}
                  isDelivering={false}
                  readOnly={isReadOnly}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="mt-4">
          {cancelledOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No cancelled orders</h3>
                  <p className="text-sm">Cancelled orders will appear here.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {cancelledOrders.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onMarkDelivered={async () => {}}
                  isDelivering={false}
                  readOnly
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnerOrders;
